import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function AcceptInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { user: currentUser, refresh } = useAuth();
    const [state, setState] = useState({ phase: "loading" });
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/invites/${token}`);
                setState({ phase: "valid", invite: data });
                setName(prev => prev || "");
            } catch (err) {
                const status = err?.response?.status;
                const detail = formatApiErrorDetail(err);
                if (status === 410) setState({ phase: "expired", message: detail || "This invitation is no longer valid" });
                else if (status === 404) setState({ phase: "invalid" });
                else setState({ phase: "error", message: detail || "Something went wrong" });
            }
        })();
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || password.length < 6) return;
        setBusy(true);
        try {
            await api.post(`/invites/${token}/accept`, { name: name.trim(), password });
            await refresh();
            setState({ phase: "success" });
            setTimeout(() => navigate("/dashboard"), 800);
        } catch (err) {
            const status = err?.response?.status;
            const detail = formatApiErrorDetail(err);
            if (status === 410) setState({ phase: "expired", message: detail });
            else if (status === 403) setState({ phase: "wrong_email", message: detail, invitedEmail: state.invite?.invited_email });
            else if (status === 409) setState({ phase: "already_registered", message: detail });
            else setState({ phase: "error", message: detail || "Something went wrong" });
        } finally { setBusy(false); }
    };

    const isSignedInMismatch = currentUser && state.invite &&
        currentUser.email?.toLowerCase() !== state.invite.invited_email?.toLowerCase();

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-6">
                    <p className="overline">ClientSpace</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-8">
                    {state.phase === "loading" && (
                        <div data-testid="accept-loading" className="flex items-center justify-center py-8 text-slate-500">
                            <Loader2 className="w-5 h-5 animate-spin" />
                        </div>
                    )}
                    {state.phase === "invalid" && (
                        <Fallback testid="accept-invalid" title="Invitation not found"
                            body="This link doesn't match any invitation. It may have been mistyped, or the invite may have been replaced. Ask whoever invited you to send a fresh one." />
                    )}
                    {state.phase === "expired" && (
                        <Fallback testid="accept-expired" title="Invitation no longer valid"
                            body={state.message || "This invitation has expired, been revoked, or already been used. Ask the workspace owner to send a new one."} />
                    )}
                    {state.phase === "wrong_email" && (
                        <Fallback testid="accept-wrong-email" title="Signed in as a different account"
                            body={`This invitation was sent to ${state.invitedEmail || "another address"}. Sign out and try again to accept it as the invited person.`} />
                    )}
                    {state.phase === "already_registered" && (
                        <Fallback testid="accept-already-registered" title="Email already has a ClientSpace account"
                            body="This email is already used by another ClientSpace account. Ask the workspace owner to invite a different address." />
                    )}
                    {state.phase === "error" && (
                        <Fallback testid="accept-error" title="Something went wrong"
                            body={state.message || "The connection blipped for a moment. Try opening the link again."} />
                    )}
                    {state.phase === "success" && (
                        <div data-testid="accept-success" className="text-center py-4">
                            <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-3">
                                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            </div>
                            <p className="font-display text-2xl tracking-tight text-slate-900">You're in.</p>
                            <p className="text-sm text-slate-500 mt-2">Taking you to your new workspace…</p>
                        </div>
                    )}
                    {state.phase === "valid" && !isSignedInMismatch && (
                        <form onSubmit={submit} data-testid="accept-form" className="space-y-5">
                            <div>
                                <p className="overline mb-2">Join the team</p>
                                <h1 className="font-display text-2xl tracking-tight text-slate-900">
                                    You're invited to <span className="text-[#C55A46]">{state.invite.workspace?.company_name}</span>
                                </h1>
                                <p className="text-sm text-slate-500 mt-2">
                                    {state.invite.inviter?.name || state.invite.inviter?.email || "The workspace owner"} invited <strong>{state.invite.invited_email}</strong> as a <strong className="capitalize">{state.invite.role}</strong>.
                                </p>
                            </div>
                            <div>
                                <Label htmlFor="accept-name">Your name</Label>
                                <Input id="accept-name" type="text" required minLength={1}
                                    value={name} onChange={(e) => setName(e.target.value)}
                                    data-testid="accept-name-input"
                                    className="mt-1 ring-terra" placeholder="Ada Lovelace" />
                            </div>
                            <div>
                                <Label htmlFor="accept-password">Create a password</Label>
                                <Input id="accept-password" type="password" required minLength={6}
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    data-testid="accept-password-input"
                                    className="mt-1 ring-terra" placeholder="At least 6 characters" />
                            </div>
                            <button type="submit" disabled={busy}
                                data-testid="accept-submit"
                                className="w-full btn-terra text-sm font-medium py-2.5 rounded-lg inline-flex items-center justify-center gap-2">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                Accept invitation
                            </button>
                        </form>
                    )}
                    {state.phase === "valid" && isSignedInMismatch && (
                        <Fallback testid="accept-signed-in-mismatch"
                            title={`You're signed in as ${currentUser.email}`}
                            body={`This invitation is for ${state.invite.invited_email}. Sign out first, then reopen this invite link to accept.`} />
                    )}
                </div>
            </div>
        </div>
    );
}

function Fallback({ testid, title, body }) {
    return (
        <div data-testid={testid} className="text-center py-4">
            <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <p className="font-display text-xl tracking-tight text-slate-900">{title}</p>
            <p className="text-sm text-slate-500 mt-2">{body}</p>
        </div>
    );
}
